/**
 * The two brand strings, in one place: the line under the wordmark, and the
 * name of the page in a browser tab.
 *
 * **The plant-based claim (DECIDED 20261019, Barna), replacing the rule of
 * 20261011.** The one fixed claim *100% növényi* is replaced by four
 * permitted Hungarian forms, chosen by grammatical position:
 *
 *   * before a noun: *100% növényi alapú* + noun;
 *   * as a predicate: *100%-ban növényi*;
 *   * either, qualified in full: *100%-ban növényi alapú*;
 *   * as a verb phrase: *100% növényi alapon készül*.
 *
 * Two rules follow. Bare *100% növényi* with no *alapú* or *alapon* after it
 * is not allowed, and *növényi alapú* with no *100%* or *100%-ban* directly
 * before it is not allowed, because unqualified it is used in Hungarian for
 * products that are only mostly plant-based. `test/claim.test.ts` and
 * `apps/web/src/page/claim.test.tsx` hold both, on rendered output.
 *
 * **The brand line (DECIDED 20261021, Barna, replacing the wording of 20261020;
 * system specification §3.8).**
 * One line in two forms. Beside the logo, which carries the name, the line is
 * `BRAND_TAGLINE` and nothing else; wherever no image can be shown, it is
 * `BRAND_TITLE`, the name and the line joined by an em dash with a space
 * either side. The two languages translate each other again. English has no
 * claim rule of its own. The short form, *Planteen catering* / *The Planteen
 * catering*, is the email sender name and is not held here.
 *
 * Nothing here imports anything: `apps/web` pulls this file into the browser
 * bundle and `legalPages.ts` into the Vite build, as well as the email
 * layout, so it has to stay plain strings and nothing else.
 */

/**
 * The line under the wordmark, everywhere one is shown: the page header, the
 * email header, the footer of every email, and the legal-page header. One
 * constant, so the customer meets one line wherever they meet it — which is
 * what run 11 item 6c asked for and could not enforce while the words lived
 * in three files.
 *
 * Never in a PDF: `scripts/legal_pdf.ts` writes the name as the document
 * author and no tagline anywhere.
 */
export const BRAND_TAGLINE: Readonly<{ hu: string; en: string }> = {
  hu: '100%-ban növényi alapú catering',
  en: '100% plant-based catering',
}

/**
 * The brand line where no image can be shown, as a whole string rather than
 * a word to build one from: the tab title the page sets, the static `<title>`
 * of `apps/web/index.html`, the heading of the loading screen, and the
 * link-preview tags `og:title` and `og:site_name`. The meta description and
 * `og:description` start with it (`brandDescription`). Never an email, never
 * a PDF.
 *
 * `apps/web/index.html` is static HTML and cannot import: it carries the
 * Hungarian of this by hand, and `brand.test.ts` holds the two together.
 */
export const BRAND_TITLE: Readonly<{ hu: string; en: string }> = {
  hu: 'Planteen — 100%-ban növényi alapú catering',
  en: 'The Planteen — 100% plant-based catering',
}

/**
 * The meta description and `og:description`: `BRAND_TITLE`, then one
 * sentence on what the page is for. Built from `BRAND_TITLE` rather than
 * written out again, so the brand line still has one spelling.
 */
const DESCRIPTION_REST: Readonly<{ hu: string; en: string }> = {
  hu: 'Állítsa össze rendezvénye menüjét, és rendelje meg online.',
  en: 'Put together the menu for your event and order online.',
}

/** The tagline in the language of the thing being rendered. Hungarian
 *  wherever the language is absent, as every caller's default is. */
export const brandTagline = (lang: string | undefined): string =>
  lang === 'en' ? BRAND_TAGLINE.en : BRAND_TAGLINE.hu

/** The page title in the language on screen. */
export const brandTitle = (lang: string | undefined): string =>
  lang === 'en' ? BRAND_TITLE.en : BRAND_TITLE.hu

/** The meta description and `og:description` in the language on screen. */
export const brandDescription = (lang: string | undefined): string =>
  lang === 'en'
    ? `${BRAND_TITLE.en}. ${DESCRIPTION_REST.en}`
    : `${BRAND_TITLE.hu}. ${DESCRIPTION_REST.hu}`
